import { CustomEventWithDetail, html, View } from 'rune-ts';
import { ButtonCheckbox } from '../../../atoms/ButtonCheckbox/ButtonCheckbox';
import { ProductFilterButtonList } from './ProductFilterButtonList';
import klass from '../ProductFilterModal.module.scss';
import { typo } from '../../../../../shared/typography/typo';
import { InputNumber, InputNumberChangeEvent, InputNumberInputEvent } from '../../../atoms/Input/InputNumber';
import { ShopProductListFilterData, ProductFilterOption } from '../../../../../features/ProductList/type';
import { getMaxPriceByLanguage } from '../../../../../features/ProductList/util';

export class ProductFilterChangedEvent extends CustomEventWithDetail<ProductFilterOption> {}

export class ProductFilterConfirmEvent extends CustomEventWithDetail<ProductFilterOption> {}

export class ProductFilterBody extends View<ShopProductListFilterData> {
  MIN_PRICE: Readonly<number> = 0;
  MAX_PRICE: Readonly<number> = getMaxPriceByLanguage(ET.lang);

  MinPriceInput: InputNumber;
  MaxPriceInput: InputNumber;

  CreatorFilter: ProductFilterButtonList;
  StyleFilter: ProductFilterButtonList;

  constructor(data: ShopProductListFilterData, private filter: ProductFilterOption) {
    super(data, filter);

    this.MinPriceInput = new InputNumber({
      value: this.filter.min_price,
      name: 'min_price',
      placeholder: '0',
      min: this.MIN_PRICE,
      max: this.MAX_PRICE - 1,
      comma_seperated: true,
    });

    this.MaxPriceInput = new InputNumber({
      value: this.filter.max_price,
      name: 'max_price',
      placeholder: this.MAX_PRICE.toLocaleString(),
      min: this.MIN_PRICE,
      max: this.MAX_PRICE,
      comma_seperated: true,
    });

    this.CreatorFilter = new ProductFilterButtonList(
      this.data.creator_filters.map((f) => ({ ...f, label: f.name })),
      this.filter.creator_filter_ids ?? [],
      {
        klass: klass.creator_filter,
        btn_klass: klass.filter_btn,
      },
    );

    this.StyleFilter = new ProductFilterButtonList(
      this.data.style_filters.map((f) => ({ ...f, label: f.name })),
      this.filter.style_filter_ids ?? [],
      {
        klass: klass.style_filter,
        btn_klass: klass.filter_btn,
      },
    );
  }

  clear() {
    this.MinPriceInput.reset();
    this.MaxPriceInput.reset();
    // this.IsNotSoldOutCheckbox.deactivate();
    // this.IsLimitedCheckbox.deactivate();
    this.CreatorFilter.clear();
    this.StyleFilter.clear();
  }

  isValueSelected(): boolean {
    const {
      min_price,
      max_price,
      // without_sold_out,
      // is_limited,
      creator_filter_ids,
      style_filter_ids,
    } = this.getValue();

    const res =
      (min_price && min_price > this.MIN_PRICE) ||
      (max_price && max_price > this.MIN_PRICE) ||
      // without_sold_out ||
      // is_limited ||
      !!creator_filter_ids?.length ||
      !!style_filter_ids?.length;

    return res ?? false;
  }

  override template() {
    return html`<form class="${klass.filter}">
      <div class="${klass.form_item}">
        <span class="${klass.title} ${typo('14_medium')}">${ET('mps2::product::price')}</span>
        <div class="${klass.price}">
          ${this.MinPriceInput}
          <span class="${typo('14_medium')}">~</span>
          ${this.MaxPriceInput}
        </div>
      </div>

      <div class="${klass.filter_container}">
        <span class="${klass.title} ${typo('14_medium')}">${ET('mps2::creator::creator_01')}</span>
        ${this.CreatorFilter}
      </div>

      <div class="${klass.filter_container}">
        <span class="${klass.title} ${typo('14_medium')}">${ET('mps2::product::filter::style')}</span>
        ${this.StyleFilter}
      </div>
    </form>`;
  }

  override onRender() {
    this.delegate(InputNumberInputEvent, InputNumber, () => {
      this.dispatchEvent(ProductFilterChangedEvent, {
        bubbles: true,
        detail: this.filter,
      });
    });

    this.delegate(InputNumberChangeEvent, InputNumber, (e) => {
      const value = e.detail.value;
      if (!value) {
        return;
      }
      const { min_price, max_price } = this.getValue();

      // swap min & max price
      if (min_price && max_price && min_price > max_price) {
        this.MinPriceInput.setValue(max_price);
        this.MaxPriceInput.setValue(min_price);
      }

      this.dispatchEvent(ProductFilterChangedEvent, {
        bubbles: true,
        detail: this.filter,
      });
    });

    this.delegate('click', ButtonCheckbox, (_, view) => {
      if (view.isChecked()) {
        view.activate();
      } else {
        view.deactivate();
      }
      this.dispatchEvent(ProductFilterChangedEvent, {
        bubbles: true,
        detail: this.filter,
      });
    });

    this.delegate('click', ProductFilterButtonList, () => {
      this.dispatchEvent(ProductFilterChangedEvent, {
        bubbles: true,
        detail: this.filter,
      });
    });
  }

  getValue(): ProductFilterOption {
    return {
      min_price: this.MinPriceInput.getValue(),
      max_price: this.MaxPriceInput.getValue(),
      // without_sold_out: this.IsNotSoldOutCheckbox.data.active,
      // is_limited: this.IsLimitedCheckbox.data.active,
      creator_filter_ids: [...this.CreatorFilter.selected],
      style_filter_ids: [...this.StyleFilter.selected],
    };
  }

  setValue(filter: ProductFilterOption) {
    this.filter = filter;

    if (filter.min_price) {
      this.MinPriceInput.setValue(filter.min_price);
    } else {
      this.MinPriceInput.reset();
    }

    if (filter.max_price) {
      this.MaxPriceInput.setValue(filter.max_price);
    } else {
      this.MaxPriceInput.reset();
    }

    // if (filter.is_limited) {
    //   this.IsLimitedCheckbox.activate();
    // } else {
    //   this.IsLimitedCheckbox.deactivate();
    // }
    //
    // if (filter.without_sold_out) {
    //   this.IsNotSoldOutCheckbox.activate();
    // } else {
    //   this.IsNotSoldOutCheckbox.deactivate();
    // }

    this.CreatorFilter.setSelected(filter.creator_filter_ids ?? []);
    this.StyleFilter.setSelected(filter.style_filter_ids ?? []);
  }
}
